import React, { useEffect, useState, memo, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Dialog, DialogTitle,
  DialogActions, DialogContent, InputAdornment, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import InfoButton from "../../../components/Tooltip/InfoTextField";
import DefaultActions from "../../../components/Card/DefaultActions";
import { AccountViewModel } from "../../../Types/ExportTypes";
import {
  ProductPostingGroupAutoComplete, AccountTypeAutoComplete, OssTypeAutoComplete
} from "../../../components/AutoComplete/AutoCompletes";

import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const AccountEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { t } = useTranslation();
  const { accountNo = "" } = params || {};
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(AccountViewModel & { hasChanges?: boolean; originalValues?: Partial<AccountViewModel>; }) | null>(null);
  const [showSettings, setContactSettings] = useState(false);
  const [isCopyModalOpen, setIsCopyModalOpen] = useState(false);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);


  useEffect(() => {
    isLoading ? showLoading() : hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (accountNo) {
      setIsLoading(true);
      authService
        .get<AccountViewModel>(`/settingsApi/GetAccount?accountNo=${accountNo}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultAccount: AccountViewModel & { originalValues: Partial<AccountViewModel>; hasChanges: boolean } = {
        createNew: true,
        type: "0",
        originalValues: {},
        hasChanges: false,
      } as AccountViewModel & { originalValues: Partial<AccountViewModel>; hasChanges: boolean };
      defaultAccount.originalValues = { ...defaultAccount };
      setViewModel(defaultAccount);
    }
  }, [accountNo, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return;

    setIsLoading(true);
    try {
      const createNew = viewModel.createNew;
      const response = await authService.post<AccountViewModel>("/settingsApi/CreateOrUpdateAccount", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (createNew === true) {
        navigate({ path: "/settings/account/edit", params: { accountNo: response.accountNo } });
      }
    } catch {
      showSnackbar(t("dialog.savedSuccesfully"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof AccountViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as AccountViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.accountDataMissing"), "error");
      return false;
    }
    if (!viewModel.accountNoDesc?.trim()) {
      showSnackbar(t("dialog.accountNoRequired"), "warning");
      return false;
    }
    if (!viewModel.description?.trim()) {
      showSnackbar(t("dialog.accountDescriptionRequired"), "warning");
      return false;
    }
    if (!viewModel.type?.trim()) {
      showSnackbar(t("dialog.accountTypeRequired"), "warning");
      return false;
    }



    return true;
  };


  const handleCopy = async (newAccountNo?: string, newDescription?: string) => {
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryAccount"), "warning");
      return;
    }

    if (newAccountNo && newAccountNo === viewModel?.accountNoDesc) {
      showSnackbar(t("dialog.accountNoSameAsSource"), "warning");
      return;
    }
    // If newCode is provided, update viewModel and continue
    if (newAccountNo && newDescription) {
      setViewModel(prev => ({ ...prev, newAccountNo, newDescription }));
    }


    // If newCode isn't set, open the modal and return
    if (!viewModel?.newAccountNo && !newAccountNo) {
      setIsCopyModalOpen(true);
      return;
    }
    setIsCopyModalOpen(false);
    setIsLoading(true);
    try {
      var response = await authService.post<AccountViewModel>(`/settingsApi/CopyAccount`, { ...viewModel, newAccountNo, newDescription });
      setViewModel(response);
      showSnackbar(t("dialog.accountCopiedSuccess"), "success");

      navigate({ path: "/settings/account/edit", params: { accountNo: response.accountNo } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.accountCopyError")} : ${error.response?.data ?? error.message}`, "error");
      setViewModel(prev => ({ ...prev, newCode: null }));

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/account/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteAccount`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");

      navigate({ path: "/settings/account/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };





  type AccountInputModalProps = {
    open: boolean;
    onClose: () => void;
    onConfirm: (newCode: string, newDescription: string) => void;
    ViewModel?: AccountViewModel | null
  };
  const AccountNoInputModal = memo(({ open, onClose, onConfirm, ViewModel }: AccountInputModalProps) => {
    const [tempAccountNo, setTempAccountNo] = useState("");
    const [tempDescription, setTempDescription] = useState("");
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>{t("common.enterNewAccountNo")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newAccountNo")}
            fullWidth
            variant="outlined"
            value={tempAccountNo}
            onChange={(e) => setTempAccountNo(e.target.value)}
            inputProps={{ maxLength: 20 }}
            error={ViewModel?.accountNoDesc === tempAccountNo || (!tempAccountNo || tempAccountNo.trim() === "")}
            helperText={
              ViewModel?.accountNoDesc === tempAccountNo
                ? t("help.accountNoSameAsSource")
                : (!tempAccountNo?.trim())
                  ? t("help.accountNoRequired")
                  : ""
            }
          />
          {/*error={!viewModel.shopNo || viewModel.shopNo.trim() === ""}*/}
          {/*helperText={!viewModel.shopNo || viewModel.shopNo.trim() === "" ? "Shop is required" : ""}*/}
          <TextField
            autoFocus
            margin="dense"
            label={t("common.newAccountDescription")}
            fullWidth
            variant="outlined"
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            inputProps={{ maxLength: 30 }}
            error={(!tempDescription || tempDescription.trim() === "")}
            helperText={
              !tempDescription?.trim() ? t("help.accountDescriptionRequired") : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}> {t("common.close")}</Button>
          <Button
            onClick={() => {
              if (!tempAccountNo.trim()) {
                showSnackbar(t("dialog.accountNoEmpty"), "warning");
                return;
              }
              if (!tempDescription.trim()) {
                showSnackbar(t("dialog.accountDescriptionEmpty"), "warning");
                return;
              }
              onConfirm(tempAccountNo?.trim(), tempDescription?.trim()); // Pass only on confirm
            }}
            color="primary"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  return (
    <>
      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.account")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
          <DefaultActions onSave={handleSave} onDelete={handleDelete} onCopy={handleCopy} />
        </Box>
        <AccountNoInputModal open={isCopyModalOpen} onClose={() => setIsCopyModalOpen(false)} onConfirm={handleCopy} ViewModel={viewModel} />
        {viewModel && (
          <Grid container spacing={2}>

            <Grid item xs={12}>
              <TextField
                label={t("common.accountNo")}
                value={viewModel.accountNoDesc || ""}
                onChange={(e) => handleChange("accountNoDesc", e.target.value)}
                fullWidth
                margin="normal"
                inputProps={{ maxLength: 20 }}

                error={!viewModel.accountNoDesc || viewModel.accountNoDesc.trim() === ""}
                helperText={
                  !viewModel.accountNoDesc?.trim()
                    ? t("help.accountNoRequired")
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("common.description")}
                value={viewModel.description || ""}
                onChange={(e) => handleChange("description", e.target.value)}
                fullWidth
                margin="normal"
                inputProps={{ maxLength: 30 }}
                error={!viewModel.description || viewModel.description.trim() === ""}
                helperText={!viewModel.description || viewModel.description.trim() === "" ? t("dialog.descriptionRequired") : ""}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <AccountTypeAutoComplete
                  key={viewModel.type || null}
                  valueId={viewModel.type || null}
                  onChange={(selected) => handleChange("type", selected?.value || "")}
                  error={!viewModel.type || viewModel.type.trim() === ""}
                  helperText={
                    !viewModel.type?.trim()
                      ? t("help.accountTypeRequired")
                      : ""
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <ProductPostingGroupAutoComplete
                  key={viewModel.productPostingGroupKey || null}
                  valueId={viewModel.productPostingGroupKey || null}
                  onChange={(selected) => handleChange("productPostingGroupKey", selected?.value || "")}
                  error={!viewModel.productPostingGroupKey || viewModel.productPostingGroupKey.trim() === ""}
                  helperText={
                    !viewModel.productPostingGroupKey?.trim()
                      ? t("help.postingSetupWhatRequired")
                      : ""
                  }
                />
              </FormControl>
            </Grid>




            <Grid item xs={12}>

              <FormControlLabel
                control={<Switch checked={showSettings} onChange={(e) => setContactSettings(e.target.checked)} />}
                label={t("common.showSettings")}
              />
            </Grid>


            {showSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!viewModel.systemLocked}
                          onChange={(e) => handleChange("systemLocked", e.target.checked)}
                        />
                      }
                      label={t("common.locked")}
                    />
                  </Grid>


                  <Grid item xs={12}>
                    <TextField
                      label={t("common.vatDeclarationCode")}
                      value={viewModel.vatDeclarationCode || ""}
                      onChange={(e) => handleChange("vatDeclarationCode", e.target.value)}
                      fullWidth
                      margin="normal"
                      inputProps={{ maxLength: 50 }}
                    />
                  </Grid>

                  {/*<FormControl fullWidth margin="normal">*/}
                  {/*    <OssTypeAutoComplete*/}
                  {/*        key={viewModel.ossType || null}*/}
                  {/*        valueId={viewModel.ossType || null}*/}
                  {/*        onChange={(selected) => handleChange("ossType", selected?.value || "")}*/}
                  {/*    />*/}
                  {/*</FormControl>*/}

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.sru")}
                      value={viewModel.sru || ""}
                      onChange={(e) => handleChange("sru", e.target.value)}
                      fullWidth
                      margin="normal"
                      inputProps={{ maxLength: 50 }}
                    />
                  </Grid>



                </Box>
              </>
            )}



          </Grid>
        )}
        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>
      </CenteredCard>


    </>
  );
};

export default AccountEditPage;
